import React, { FC, createContext, useContext, ReactNode, useMemo } from 'react';
import { useCompanyLicenses, useSelector } from '~Hooks';
import { BAEMINGO_PRODUCT, PRODUCT_TYPE } from '../../../__queries__/globalTypes';
import { useQuery } from '@apollo/client';
import { ACTIVE_LICENSES_QUERY } from './api/queries';
import { GetActiveLicenses } from './api/__queries__';
import { useDeviceHandler } from '~Components/DeviceProvider';

/**
 * This interface tracks two categories of licenses:
 * 1. Venue-level licenses: Features available throughout the entire venue
 * 2. Device-level licenses: Features available on specific devices
 *
 * Used to determine which features should be accessible in the application based on
 * what the venue has licensed/purchased.
 *
 * isXClaimable represents if a device-level license is active on the venue but without a deviceId - meaning it can be claimed by a device
 */
interface LicensesContextType {
  /* -- Venue-level licenses -- */

  /** Mobile-app for the restaurant */
  hasApp: boolean;

  /** Giftcards */
  hasGiftcard: boolean;

  /** Inventory for tracking how much items is left */
  hasInventory: boolean;

  /* -- Device-level licenses -- */

  //** A display for customers to view their orders progress */
  hasCustomerDisplay: boolean;
  atleastOneDeviceHasCustomerDisplay: boolean; // We need to use the syncronizer OrderStatus on all devices if this is true
  isCustomerDisplayClaimable: boolean;

  /** Express POS / Self Service Checkout */
  hasEPOS: boolean;
  isEPOSClaimable: boolean;

  /** Evented Kitchen Display */
  hasKDS: boolean;
  atleastOneDeviceHasKDS: boolean; // We need to use the syncronizer OrderStatus on all devices if this is true
  isKDSClaimable: boolean;

  /** QSR/POS - Multi-QSR is included in this license */
  hasQSR: boolean;
  isQSRClaimable: boolean;

  /** Tables */
  hasTSR: boolean;
  atleastOneDeviceHasTSR: boolean; // Used for instance for Parked orders so other devices with no TSR can move orders to tables
  isTSRClaimable: boolean;

  /** Allows the user to add items to tables, but nothing else. Mostly used for smartphone "telecommanders" */
  hasHandheldOrder: boolean;
  isHandheldOrderClaimable: boolean;

  /** Only allows paying a table and splitting cheques */
  hasHandheldPay: boolean;
  isHandheldPayClaimable: boolean;

  /** Complete 1:1 feature parity with the POS */
  hasHandheldPro: boolean;
  isHandheldProClaimable: boolean;

  /** If true: Superadmin have manually frozen this venue's invoice account - will block pages that requires licenses */
  isPOSAccessBlocked: boolean;

  /** Superadmin can set this to true if there are unpaid invoices, used to show a warning before POS access is blocked */
  isInvoiceWarningActive: boolean;

  hasQueryFailed: boolean; // Whether the licenses query has failed
  loading: boolean;
  refetch: () => void;
}

const LicensesContext = createContext<LicensesContextType | undefined>(undefined);

export const useLicenses = () => {
  const context = useContext(LicensesContext);
  if (!context) {
    throw new Error('useLicenses must be used within a LicensesProvider');
  }
  return context;
};

interface Props {
  children: ReactNode;
}

/**
 * Handles licenses for the app. We have three solutions to licenses currently, the (1) is the latest & highest priority
 * (1) ActiveLicenses tables from Venue or ClientDevice
 * (2) CompanyFlags from the company
 * (3) Company.licenses
 */
export const LicensesProvider: FC<Props> = ({ children }) => {
  const deviceHandler = useDeviceHandler();
  const companyLicenses = useCompanyLicenses();

  const companyFlags = useSelector((state) => state.companyFlags);

  /** Fetches licenses on venue-level or licenses not assigned to any device */
  const venueLicensesQuery = useQuery<GetActiveLicenses>(ACTIVE_LICENSES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const licensesOnVenue =
    venueLicensesQuery.data?.operativeViewer?.activeLicenses.map((license) => license.product) ||
    [];
  const activeDeviceLicenses =
    deviceHandler.activeDevice?.activeLicenses.map((license) => license.product) || [];

  const isPOSAccessBlocked = venueLicensesQuery.data?.operativeViewer?.isPOSAccessBlocked;
  const isInvoiceWarningActive = venueLicensesQuery.data?.operativeViewer?.isInvoiceWarningActive;

  const atleastOneDeviceHasLicense = (product: BAEMINGO_PRODUCT): boolean => {
    return deviceHandler.allDevices.some((device) =>
      device.activeLicenses.some((license) => license.product === product),
    );
  };

  const contextValue = useMemo(
    () => ({
      /* Venue-level licenses */
      hasApp: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.APP)
        : companyFlags.hasAppOrders,
      hasGiftcard: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.GIFTCARD)
        : companyLicenses.hasActiveLicense(PRODUCT_TYPE.GIFTCARD),
      hasInventory: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.INVENTORY)
        : companyFlags.hasInventory,

      /* Device-level licenses */
      hasCustomerDisplay: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.CUSTOMER_DISPLAY)
        : companyFlags.hasCDS,
      atleastOneDeviceHasCustomerDisplay: companyFlags.useNewLicenses
        ? atleastOneDeviceHasLicense(BAEMINGO_PRODUCT.CUSTOMER_DISPLAY)
        : companyFlags.hasCDS,
      isCustomerDisplayClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.CUSTOMER_DISPLAY)
        : false,

      hasEPOS: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.EPOS)
        : companyFlags.hasExpress,
      isEPOSClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.EPOS)
        : false,

      hasKDS: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.KDS)
        : companyFlags.hasKDS,
      atleastOneDeviceHasKDS: companyFlags.useNewLicenses
        ? atleastOneDeviceHasLicense(BAEMINGO_PRODUCT.KDS)
        : companyFlags.hasKDS,
      isKDSClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.KDS)
        : false,

      hasQSR: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.QSR)
        : companyFlags.hasPOS,
      isQSRClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.QSR)
        : false,

      hasTSR: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.TSR)
        : companyFlags.hasTables,
      atleastOneDeviceHasTSR: companyFlags.useNewLicenses
        ? atleastOneDeviceHasLicense(BAEMINGO_PRODUCT.TSR)
        : companyFlags.hasTables,
      isTSRClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.TSR)
        : false,

      hasHandheldOrder: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.HANDHELD_ORDER)
        : companyLicenses.hasActiveLicense(PRODUCT_TYPE.HANDHELD_ORDER),
      isHandheldOrderClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.HANDHELD_ORDER)
        : false,

      hasHandheldPay: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.HANDHELD_PAY)
        : companyLicenses.hasActiveLicense(PRODUCT_TYPE.HANDHELD_PAY),
      isHandheldPayClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.HANDHELD_PAY)
        : false,

      hasHandheldPro: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.HANDHELD_PRO)
        : companyLicenses.hasActiveLicense(PRODUCT_TYPE.HANDHELD_PRO),
      isHandheldProClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.HANDHELD_PRO)
        : false,

      isPOSAccessBlocked: companyFlags.useNewLicenses ? Boolean(isPOSAccessBlocked) : false,
      isInvoiceWarningActive: companyFlags.useNewLicenses
        ? Boolean(isInvoiceWarningActive)
        : false,

      hasQueryFailed:
        companyFlags.useNewLicenses && !venueLicensesQuery.data && !venueLicensesQuery.loading,
      loading: companyLicenses.loading || venueLicensesQuery.loading,
      refetch: () => {
        venueLicensesQuery.refetch();
        deviceHandler.refetch();
      },
    }),
    [
      companyFlags,
      companyLicenses,
      licensesOnVenue,
      activeDeviceLicenses,
      isPOSAccessBlocked,
      deviceHandler.allDevices,
      venueLicensesQuery.loading,
      companyLicenses.loading,
    ],
  );

  return <LicensesContext.Provider value={contextValue}>{children}</LicensesContext.Provider>;
};

export default LicensesProvider;
